// Port "HDRI + Film-Look" — aus Jans Renderer uebernommen.
//
// HERKUNFT
//
// pro-editor/src/storyforger/Renderer/RendererWebGL.ts — sechs Methoden
// (syncEnvironment, ensureSkyDome, applyEnvironment, bakeEnvironmentPmrem,
// renderRotatedEquirect, disposeEnvironmentTargets). Zusammen ~350 Zeilen.
//
// "Film-Look" ist KEIN Post-Processing-Stack (kein EffectComposer/Bloom/
// Vignette/Grain). Der Renderer laeuft global auf NoToneMapping; das einzige
// filmische ist die ACES-Filmic-Tonemap IM Skydome-Shader (ensureSkyDome), die
// die HDR-Range des sichtbaren Hintergrunds sanft nach Weiss ausrollen laesst.
// Der Port ist also das HDRI-Environment-Subsystem: Image-Based-Lighting
// (PMREM -> scene.environment) + optionaler sichtbarer Skydome-Hintergrund.
//
// WARUM ALS EIGENES MODUL
//
// Das Subsystem ist stark isoliert: getrieben allein von presentationOptions,
// alle Zustaende liegen in ~16 Host-Feldern (unten als EnvironmentHost). Die
// einzige Umformung gegenueber Jans Fassung ist `this.` -> `host.` bzw. freie
// Funktion — maschinell gegenpruefbar.
//
// GATE / ADDITIV-SICHERHEIT (Befund PORT-HDRI-BEFUND-2026-07-22)
//
// syncEnvironment early-outed bei leerem environmentHdri (setzt einmalig
// scene.environment = null). Die 165-Korpus-Scrollys haben kein HDRI -> No-op.
// Der per-Frame-envMapIntensity-Scan beruehrt nur Materialien mit abweichendem
// Wert (Default 1 == Default 1). Kein Bruch am Bestand.
//
// PORT-ADAPTIONEN (Version-Drift Jan r139 -> unser three 0.183.2)
//  (1) texture.encoding = THREE.sRGBEncoding  ->  texture.colorSpace =
//      THREE.SRGBColorSpace  (sRGBEncoding ist in 0.183 entfernt).
//  (2) isLocalMediaUrl/resolveLocalMediaUrl (Jans Editor-Blob-Aufloesung) haben
//      wir nicht — unsere HDRI-URLs sind per Adapter bereits aufgeloest, der
//      Zweig entfaellt (direkter build(hdri)).
//  (3) scalarChannel aus unserem Layer3D (statt Jans gleichnamiger Funktion).
//
// WAS DER PORT NICHT LEISTET
//
// Das Ergebnis ist WebGL — ob Reflexionen/Skydome korrekt aussehen, sagt kein
// Test. Die Pruefung ist visuell (Devview /__dev__/hdri-environment).
import THREE, { RGBELoader, EXRLoader } from "../utils/THREE";
import { scalarChannel } from "../Player/Layer3D";
import { istMobilesGeraet } from "../utils/geraet";
import { logError } from "../utils/logger";
import { WebGLAssets } from "../Player/webglAssets";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TODOAny = any;

/**
 * Der Teil des Renderers, den das HDRI-Subsystem anfasst. Alle Felder sind
 * veraenderlich — die Funktionen schreiben Cache-/Loading-Zustand zurueck.
 */
export interface EnvironmentHost {
    webGlScene: THREE.Scene | undefined;
    webGLRenderer: THREE.WebGLRenderer | undefined;
    pmremGenerator: THREE.PMREMGenerator | undefined;
    environmentTexture: THREE.Texture | undefined;
    environmentEquirect: THREE.Texture | undefined;
    envSourceTexture: THREE.Texture | undefined;
    envRotatedRT: THREE.WebGLRenderTarget | undefined;
    pmremRT: THREE.WebGLRenderTarget | undefined;
    envBlitScene: THREE.Scene | undefined;
    envBlitCamera: THREE.OrthographicCamera | undefined;
    envBlitMaterial: THREE.ShaderMaterial | undefined;
    skyDome: THREE.Mesh | undefined;
    skyDomeMaterial: THREE.ShaderMaterial | undefined;
    appliedEnvironmentHdri: string;
    appliedEnvironmentIntensity: number;
    appliedEnvironmentRotation: number;
    pmremBakedRotation: number;
    environmentLoading: boolean;
}

// 3D World image-based lighting. Turns the uploaded HDRI into a PMREM
// environment and assigns it to scene.environment so every PBR material is lit
// and reflects it (metals especially). Reflections/lighting only — the scene
// background is left untouched. Called every frame; only acts on change.
export function syncEnvironment(host: EnvironmentHost, presentationOptions: TODOAny, timer = 0) {
    if (!host.webGlScene || !host.webGLRenderer) return;
    const hdri: string = presentationOptions.environmentHdri ?? "";
    const intensity: number = presentationOptions.environmentIntensity ?? 1;
    // dome rotation: the keyframe channel drives it over the scroll when active,
    // otherwise the static value
    const rotation: number =
        scalarChannel(presentationOptions.environmentRotationAnim, timer) ??
        presentationOptions.environmentRotation ??
        0;

    if (hdri !== host.appliedEnvironmentHdri && !host.environmentLoading) {
        host.appliedEnvironmentHdri = hdri;
        if (host.envSourceTexture) {
            host.envSourceTexture.dispose();
            host.envSourceTexture = undefined;
        }
        host.environmentTexture = undefined;
        host.environmentEquirect = undefined;
        if (!hdri) {
            host.webGlScene.environment = null;
            disposeEnvironmentTargets(host);
        } else {
            host.environmentLoading = true;
            // pick the decoder by file type: .exr → EXRLoader, Radiance .hdr →
            // RGBELoader, and jpg/jpeg/png/webp → the plain TextureLoader (low
            // dynamic range: works as an equirect backdrop/reflection, just with
            // flatter lighting than a true HDR). Unknown/empty names stay on .hdr.
            const envName = (presentationOptions.environmentHdriName || "").toLowerCase();
            const isExr = envName.endsWith(".exr");
            const isLdr = /\.(jpe?g|png|webp)$/.test(envName);
            const build = (url: string) => {
                const onLoad = (texture: TODOAny) => {
                    try {
                        // Mobile-Deckel (Jans iPhone-Absturz 26.08.2026): ein
                        // 8K-HDRI liegt als Half-Float-RGBA mit ~268 MB auf der
                        // GPU — plus dieselbe Groesse nochmal fuer das
                        // Rotations-Target (dessen Masse der Quelle folgen).
                        // WebKit killt den Tab weit vorher. Auf Touch-Geraeten
                        // auf 2048 Texel Breite dezimieren; Beleuchtung (PMREM)
                        // und Handy-Hintergrund tragen das ohne sichtbaren
                        // Verlust.
                        texture = verkleinereEquirectFuerMobil(texture);
                        texture.mapping = THREE.EquirectangularReflectionMapping;
                        // horizontal wrap so the rotation blit is seamless at the seam
                        texture.wrapS = THREE.RepeatWrapping;
                        // LDR images are sRGB-encoded (HDR is linear).
                        // Port-Adaption (1): r139 nutzte texture.encoding =
                        // sRGBEncoding; in 0.183 ist das colorSpace/SRGBColorSpace.
                        if (isLdr) {
                            texture.colorSpace = THREE.SRGBColorSpace;
                            // No mipmaps: TextureLoader defaults to mipmapped trilinear
                            // filtering, and mipmap generation doesn't wrap — so a visible
                            // vertical seam appears at the equirect wrap edge. The HDR
                            // loaders skip mipmaps (hence no seam there); match that.
                            texture.minFilter = THREE.LinearFilter;
                            texture.generateMipmaps = false;
                        }
                        if (!host.pmremGenerator) {
                            host.pmremGenerator = new THREE.PMREMGenerator(host.webGLRenderer!);
                            host.pmremGenerator.compileEquirectangularShader();
                        }
                        host.envSourceTexture = texture;
                        host.appliedEnvironmentRotation = rotation;
                        applyEnvironment(host, rotation);
                        // force the intensity to be (re)applied next frame
                        host.appliedEnvironmentIntensity = NaN;
                    } catch (e) {
                        logError(e as Error);
                    }
                    host.environmentLoading = false;
                };
                const onErr = (err: TODOAny) => {
                    host.environmentLoading = false;
                    logError(err as Error);
                };
                if (isLdr) {
                    new THREE.TextureLoader().load(url, onLoad, undefined, onErr);
                } else {
                    const EnvLoader = isExr ? EXRLoader : RGBELoader;
                    new EnvLoader().load(url, onLoad, undefined, onErr);
                }
            };
            // Jans Original-Zweig, via WebGLAssets-Hook statt Direktimport
            // (Paketgrenze): Der EDITOR speichert Uploads als "local-hdri:<id>"
            // in der IndexedDB — der Adapter stellt die Aufloesung bereit. Ohne
            // den Zweig lief jeder HDRI-Upload still ins Leere (Jans Fund
            // "HDRI wird nicht im Renderer angezeigt"). Reader: Hook ist null,
            // veroeffentlichte URLs laufen weiter direkt in build().
            if (hdri.startsWith("local-") && WebGLAssets.resolveLocalMediaUrl) {
                WebGLAssets.resolveLocalMediaUrl(hdri).then((resolved) => {
                    if (resolved) build(resolved);
                    else host.environmentLoading = false;
                });
            } else {
                build(hdri);
            }
        }
    } else if (
        hdri &&
        !host.environmentLoading &&
        host.envSourceTexture &&
        rotation !== host.appliedEnvironmentRotation
    ) {
        host.appliedEnvironmentRotation = rotation;
        // cheap, every frame: rotate the visible dome (a single GPU blit) so the
        // animation stays smooth
        renderRotatedEquirect(host, rotation);
        if (host.envRotatedRT) host.environmentEquirect = host.envRotatedRT.texture;
        // expensive: re-bake the PMREM reflections/IBL only every few degrees — a
        // few degrees of lag in chrome reflections is imperceptible next to the
        // smoothly spinning dome, and avoids a per-frame cubemap bake
        if (!Number.isFinite(host.pmremBakedRotation) || Math.abs(rotation - host.pmremBakedRotation) >= 4) {
            bakeEnvironmentPmrem(host);
            host.pmremBakedRotation = rotation;
        }
    }

    // Apply the env intensity to every PBR material. Runs every frame but only
    // touches materials whose value is out of sync — so materials created AFTER
    // the intensity was first set (async layer loads, chess rebuilds, new
    // slides…) self-heal instead of keeping the default 1 (which looked
    // "overexposed" and appeared not to persist across reloads). The per-frame
    // scan is cheap; recompiles happen only for the few out-of-sync materials.
    host.appliedEnvironmentIntensity = intensity;
    host.webGlScene.traverse((o: TODOAny) => {
        if (!o.isMesh) return;
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m: TODOAny) => {
            if (!m || !("envMapIntensity" in m)) return;
            // Materialien mit EIGENEM "Umgebungs-Map-Intensitaet"-Regler
            // (updateMaterial stasht ihn in userData) nicht platt-syncen,
            // sondern global x eigenem Wert rechnen — sonst drueckt dieser
            // Scan den Material-Regler jeden Frame auf den globalen Wert
            // zurueck (Jans Fund: "Slider tut sich wenig").
            const own = m.userData?.__envIntensityOwn;
            const target = own !== undefined ? intensity * own : intensity;
            if (m.envMapIntensity !== target) {
                m.envMapIntensity = target;
                m.needsUpdate = true;
            }
        });
    });

    // optional visible background: a real dome drawn by our own skydome mesh
    // (see ensureSkyDome). scene.background stays null so r139 doesn't stretch
    // the equirect across a flat quad.
    const scale: number = presentationOptions.environmentBackgroundScale ?? 1;
    const brightness: number = presentationOptions.environmentBackgroundBrightness ?? 1;
    const bgOn = presentationOptions.environmentAsBackground && !!host.environmentEquirect;
    if (bgOn) {
        ensureSkyDome(host);
        if (host.skyDome && host.skyDomeMaterial) {
            host.skyDomeMaterial.uniforms.map.value = host.environmentEquirect;
            host.skyDomeMaterial.uniforms.uZoom.value = scale > 0.01 ? scale : 0.01;
            host.skyDomeMaterial.uniforms.uExposure.value = brightness;
            host.skyDome.visible = true;
        }
        if (host.webGlScene.background) host.webGlScene.background = null;
    } else if (host.skyDome) {
        host.skyDome.visible = false;
    }
}

// Build (once) and attach the skydome: an inverted sphere centred on the
// camera whose shader samples the rotated equirect by view direction, with a
// zoom that scales the spread around the camera's forward axis. Tone mapping +
// output encoding mirror three's own background so it matches the scene.
export function ensureSkyDome(host: EnvironmentHost) {
    if (host.skyDome || !host.webGlScene) return;
    host.skyDomeMaterial = new THREE.ShaderMaterial({
        uniforms: {
            map: { value: null },
            uZoom: { value: 1 },
            uExposure: { value: 1 },
            uCamForward: { value: new THREE.Vector3(0, 0, -1) },
        },
        side: THREE.BackSide,
        depthTest: false,
        depthWrite: false,
        fog: false,
        vertexShader: `
        varying vec3 vDir;
        void main() {
          vDir = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
        fragmentShader: `
        #include <common>
        uniform sampler2D map;
        uniform float uZoom;
        uniform float uExposure;
        uniform vec3 uCamForward;
        varying vec3 vDir;
        // ACES filmic tone-map: compresses the HDR range so a bright sky/sun
        // rolls off to white instead of clipping hard (the "overexposed" look).
        vec3 acesFilmic(vec3 x) {
          const float a = 2.51;
          const float b = 0.03;
          const float c = 2.43;
          const float d = 0.59;
          const float e = 0.14;
          return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
        }
        void main() {
          vec3 dir = normalize(vDir);
          vec3 f = normalize(uCamForward);
          // zoom: pull the view direction toward (zoom in) or away from (zoom
          // out) the camera forward, scaling how much of the dome is in view
          dir = normalize(mix(f, dir, 1.0 / uZoom));
          float u = atan(dir.z, dir.x) * RECIPROCAL_PI2 + 0.5;
          float v = asin(clamp(dir.y, -1.0, 1.0)) * RECIPROCAL_PI + 0.5;
          // raw linear HDR sample × exposure, then a proper filmic tone-map +
          // sRGB gamma so the dome looks like the original tone-mapped HDRI
          // instead of clipped highlights / dark midtones (the renderer itself
          // uses NoToneMapping, so we do it explicitly here).
          vec3 col = texture2D(map, vec2(u, v)).rgb * uExposure;
          col = acesFilmic(col);
          col = pow(col, vec3(1.0 / 2.2));
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    host.skyDome = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 32), host.skyDomeMaterial);
    // hidden by default; syncEnvironment shows it when "show as background" is on
    // (and the reflection pass shows it only transiently for the cube capture)
    host.skyDome.visible = false;
    host.skyDome.frustumCulled = false;
    host.skyDome.renderOrder = -100000;
    const tmpPos = new THREE.Vector3();
    const tmpDir = new THREE.Vector3();
    const tmpScale = new THREE.Vector3();
    const idQuat = new THREE.Quaternion();
    host.skyDome.onBeforeRender = (_r: TODOAny, _s: TODOAny, camera: TODOAny) => {
        // Centre the dome on whichever camera is rendering and size it to stay
        // inside its far plane. Write matrixWorld directly (the renderer already
        // built world matrices before onBeforeRender) so this applies to THIS
        // draw — important because the real-time-reflection cube pass renders the
        // dome from a different camera right before the main render. Touching
        // .position/.scale instead would leak the cube camera's transform into
        // the main render and make the dome vanish.
        const far = camera.far || 1000;
        const s = Math.max(10, far * 0.5);
        camera.getWorldPosition(tmpPos);
        tmpScale.set(s, s, s);
        host.skyDome!.matrixWorld.compose(tmpPos, idQuat, tmpScale);
        camera.getWorldDirection(tmpDir);
        host.skyDomeMaterial!.uniforms.uCamForward.value.copy(tmpDir);
    };
    host.webGlScene.add(host.skyDome);
}

// Rotate the raw equirect by `rotationDeg` around the vertical axis (a simple
// horizontal UV shift) and re-bake the PMREM lighting from it. The rotated
// equirect doubles as the visible background, so lighting/reflections and the
// panorama all stay in sync.
export function applyEnvironment(host: EnvironmentHost, rotationDeg: number) {
    const src = host.envSourceTexture;
    if (!src || !host.webGLRenderer || !host.pmremGenerator || !host.webGlScene) return;
    renderRotatedEquirect(host, rotationDeg);
    if (!host.envRotatedRT) return;
    host.environmentEquirect = host.envRotatedRT.texture;
    bakeEnvironmentPmrem(host);
    host.pmremBakedRotation = rotationDeg;
}

// Re-bake the PMREM lighting/reflection map from the current rotated equirect.
// This is the expensive part of applyEnvironment, split out so the visible dome
// can rotate every frame (cheap blit) while this runs only every few degrees.
export function bakeEnvironmentPmrem(host: EnvironmentHost) {
    if (!host.environmentEquirect || !host.pmremGenerator || !host.webGlScene) return;
    const rt = host.pmremGenerator.fromEquirectangular(host.environmentEquirect, host.pmremRT);
    host.pmremRT = rt;
    host.environmentTexture = rt.texture;
    host.webGlScene.environment = rt.texture;
}

// GPU offset blit: render the source equirect into envRotatedRT, shifting it
// horizontally by rotationDeg/360 (wrapping at the seam).
/**
 * Equirect-Textur fuer Touch-Geraete auf 2048 Texel Breite dezimieren
 * (Naechster-Nachbar — fuer Umgebungslicht/Handy-Hintergrund unsichtbar,
 * spart bei 8K-HDRIs ~95 % GPU-Speicher). Desktop und kleine Texturen
 * kommen unveraendert zurueck.
 */
const MOBILE_EQUIRECT_BREITE = 2048;
export function verkleinereEquirectFuerMobil(texture: TODOAny): TODOAny {
    if (!istMobilesGeraet()) return texture;
    const img = texture?.image;
    const w: number = img?.width || 0;
    const h: number = img?.height || 0;
    if (!w || w <= MOBILE_EQUIRECT_BREITE) return texture;
    const zielW = MOBILE_EQUIRECT_BREITE;
    const zielH = Math.max(1, Math.round((h * zielW) / w));

    // LDR (jpg/png/webp): normales Bild — Canvas skaliert sauber.
    if (typeof HTMLImageElement !== "undefined" && img instanceof HTMLImageElement) {
        const canvas = document.createElement("canvas");
        canvas.width = zielW;
        canvas.height = zielH;
        canvas.getContext("2d")!.drawImage(img, 0, 0, zielW, zielH);
        const neu = new THREE.CanvasTexture(canvas);
        texture.dispose();
        return neu;
    }

    // HDR/EXR: DataTexture (Half-/Float-RGBA) — Stride-Sampling auf dem
    // Roh-Array, der Werteraum bleibt unangetastet.
    const daten = img?.data;
    if (!daten || daten.length !== w * h * 4) return texture;
    const Ctor = daten.constructor as new (n: number) => typeof daten;
    const ziel = new Ctor(zielW * zielH * 4);
    for (let y = 0; y < zielH; y += 1) {
        const qy = Math.min(h - 1, Math.floor((y / zielH) * h));
        for (let x = 0; x < zielW; x += 1) {
            const qx = Math.min(w - 1, Math.floor((x / zielW) * w));
            const qi = (qy * w + qx) * 4;
            const zi = (y * zielW + x) * 4;
            ziel[zi] = daten[qi];
            ziel[zi + 1] = daten[qi + 1];
            ziel[zi + 2] = daten[qi + 2];
            ziel[zi + 3] = daten[qi + 3];
        }
    }
    const neu = new THREE.DataTexture(ziel, zielW, zielH, THREE.RGBAFormat, texture.type);
    neu.magFilter = THREE.LinearFilter;
    neu.minFilter = THREE.LinearFilter;
    neu.generateMipmaps = false;
    // Orientierung + Farbraum der Quelle uebernehmen — ohne flipY stand der
    // Himmel auf Touch-Geraeten kopfueber (Jans Fund 26.08.2026: DataTexture
    // defaultet flipY=false, die Loader-Textur kann anders stehen).
    neu.flipY = texture.flipY;
    neu.colorSpace = texture.colorSpace;
    neu.needsUpdate = true;
    texture.dispose();
    return neu;
}

export function renderRotatedEquirect(host: EnvironmentHost, rotationDeg: number) {
    const src = host.envSourceTexture;
    if (!src || !host.webGLRenderer) return;
    const img: TODOAny = src.image;
    const w: number = img?.width || 2048;
    const h: number = img?.height || 1024;

    if (!host.envRotatedRT || host.envRotatedRT.width !== w || host.envRotatedRT.height !== h) {
        if (host.envRotatedRT) host.envRotatedRT.dispose();
        host.envRotatedRT = new THREE.WebGLRenderTarget(w, h, {
            type: THREE.HalfFloatType,
            format: THREE.RGBAFormat,
            magFilter: THREE.LinearFilter,
            minFilter: THREE.LinearFilter,
            generateMipmaps: false,
        });
        host.envRotatedRT.texture.mapping = THREE.EquirectangularReflectionMapping;
    }

    if (!host.envBlitScene) {
        host.envBlitMaterial = new THREE.ShaderMaterial({
            uniforms: {
                map: { value: null },
                offsetX: { value: 0 },
            },
            vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = vec4(position.xy, 0.0, 1.0);
          }
        `,
            fragmentShader: `
          uniform sampler2D map;
          uniform float offsetX;
          varying vec2 vUv;
          void main() {
            gl_FragColor = texture2D(map, vec2(fract(vUv.x + offsetX), vUv.y));
          }
        `,
            depthTest: false,
            depthWrite: false,
        });
        host.envBlitScene = new THREE.Scene();
        host.envBlitScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), host.envBlitMaterial));
        host.envBlitCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    }

    host.envBlitMaterial!.uniforms.map.value = src;
    host.envBlitMaterial!.uniforms.offsetX.value = (((rotationDeg % 360) + 360) % 360) / 360;

    const prevTarget = host.webGLRenderer.getRenderTarget();
    host.webGLRenderer.setRenderTarget(host.envRotatedRT);
    host.webGLRenderer.render(host.envBlitScene, host.envBlitCamera!);
    host.webGLRenderer.setRenderTarget(prevTarget);
}

export function disposeEnvironmentTargets(host: EnvironmentHost) {
    if (host.envRotatedRT) {
        host.envRotatedRT.dispose();
        host.envRotatedRT = undefined;
    }
    if (host.pmremRT) {
        host.pmremRT.dispose();
        host.pmremRT = undefined;
    }
}
